import { useSyncExternalStore } from 'react';

/**
 * The current time, re-read every thirty seconds.
 *
 * A countdown computed once per render froze: "in 5m" stayed on screen long
 * after the deadline had passed, because nothing re-rendered a list that was
 * not being touched. One shared timer drives every subscriber, so a page of
 * fifty rows costs one interval, not fifty.
 */

const TICK_MS = 30_000;

const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;
let current = Date.now();

function tick(): void {
  current = Date.now();
  for (const notify of listeners) notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === undefined) timer = setInterval(tick, TICK_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}

function getSnapshot(): number {
  // Nothing ticks while nobody is subscribed, so the stored value can be
  // minutes old when the first countdown mounts. It is refreshed only once it
  // is a whole tick out of date: React reads the snapshot twice in one render
  // and requires both reads to agree, which reading the clock every time would
  // break.
  // (Absolute, so a clock that has been set back counts as out of date too.)
  if (listeners.size === 0 && Math.abs(Date.now() - current) >= TICK_MS) current = Date.now();
  return current;
}

export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
